import React, { useMemo, useState } from 'react';
import { UtensilsCrossed, X, Search, Save, Loader2, Info } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { MODAL_OVERLAY_Z40_CLASS } from '../../ui/modalOverlay';
import { JOURS_SEMAINE, REGIMES_FACTURATION, formatMontant } from '../cantineUtils';

const STATUT_OPTIONS = [
  { value: 'actif', label: 'Actif' },
  { value: 'suspendu', label: 'Suspendu' },
  { value: 'resilie', label: 'Résilié' }
];

const ModalInscriptionCantine = ({
  isOpen,
  onClose,
  mode,
  value,
  setValue,
  eleves,
  elevesDejaInscrits,
  isSaving,
  onSubmit
}) => {
  const [searchEleve, setSearchEleve] = useState('');
  const isEdit = mode === 'edit';

  const elevesDisponibles = useMemo(() => {
    if (isEdit) return [];
    const dejaInscrits = new Set(elevesDejaInscrits.map((id) => String(id)));
    const terme = searchEleve.trim().toLowerCase();

    return eleves
      .filter((eleve) => !dejaInscrits.has(String(eleve.id)))
      .filter((eleve) => {
        if (!terme) return true;
        return [eleve.nom, eleve.prenom, eleve.matricule, eleve.classe_nom]
          .some((champ) => String(champ || '').toLowerCase().includes(terme));
      })
      .slice(0, 60);
  }, [eleves, elevesDejaInscrits, searchEleve, isEdit]);

  if (!isOpen) return null;

  const toggleJour = (jour) => {
    const jours = value.jours_semaine || [];
    setValue({
      ...value,
      jours_semaine: jours.includes(jour)
        ? jours.filter((j) => j !== jour)
        : [...jours, jour].sort((a, b) => a - b)
    });
  };

  const eleveSelectionne = eleves.find((e) => String(e.id) === String(value.eleve_id));

  return (
    <div className={`${MODAL_OVERLAY_Z40_CLASS} flex items-center justify-center p-4`}>
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto">
        <div className="bg-[#246990] p-6 flex items-center justify-between sticky top-0 z-10">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-white/80">Cantine scolaire</p>
            <h3 className="text-xl font-semibold text-white flex items-center gap-3">
              <UtensilsCrossed size={24} />
              {isEdit ? "Modifier l'inscription" : 'Inscrire un élève à la cantine'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
            disabled={isSaving}
          >
            <X size={22} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Sélection de l'élève */}
          {isEdit ? (
            <div className="rounded-lg border-2 border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-semibold uppercase text-gray-500">Élève</p>
              <p className="mt-1 text-lg font-bold text-gray-900">
                {value.eleve_prenom} {value.eleve_nom}
              </p>
              <p className="text-sm text-gray-600">
                {value.classe_nom || '—'} · {value.matricule || 'Sans matricule'}
              </p>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Élève à inscrire <span className="text-red-500">*</span>
              </label>
              <div className="relative mb-2">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Rechercher par nom, matricule ou classe..."
                  value={searchEleve}
                  onChange={(e) => setSearchEleve(e.target.value)}
                  className="pl-9"
                />
              </div>

              <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-200">
                {elevesDisponibles.length === 0 ? (
                  <p className="p-4 text-center text-sm text-gray-500">
                    {eleves.length === 0
                      ? 'Aucun élève chargé.'
                      : 'Aucun élève disponible : tous les élèves correspondants sont déjà inscrits.'}
                  </p>
                ) : (
                  elevesDisponibles.map((eleve) => {
                    const selectionne = String(eleve.id) === String(value.eleve_id);
                    return (
                      <button
                        key={eleve.id}
                        type="button"
                        onClick={() => setValue({ ...value, eleve_id: eleve.id })}
                        className={`w-full text-left px-3 py-2.5 border-b last:border-b-0 transition-colors ${
                          selectionne ? 'bg-[#246990]/10' : 'hover:bg-gray-50'
                        }`}
                      >
                        <p className={`text-sm ${selectionne ? 'font-bold text-[#246990]' : 'font-medium text-gray-900'}`}>
                          {eleve.prenom} {eleve.nom}
                        </p>
                        <p className="text-xs text-gray-500">
                          {eleve.classe_nom || 'Sans classe'} · {eleve.matricule || 'Sans matricule'}
                        </p>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Régime de facturation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Régime de facturation <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {REGIMES_FACTURATION.map((regime) => {
                const selectionne = value.regime_facturation === regime.value;
                return (
                  <button
                    key={regime.value}
                    type="button"
                    onClick={() => setValue({ ...value, regime_facturation: regime.value })}
                    className={`text-left rounded-lg border-2 p-4 transition-colors ${
                      selectionne
                        ? regime.value === 'forfait'
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-purple-400 bg-purple-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <p className="text-sm font-semibold text-gray-900">{regime.label}</p>
                    <p className="text-xs text-gray-600 mt-1">{regime.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Jours de la semaine -- forfait uniquement */}
          {value.regime_facturation === 'forfait' && (
            <div className="border-2 border-blue-200 rounded-lg bg-blue-50/30">
              <div className="p-4 bg-blue-50 border-b border-blue-200">
                <h4 className="text-base font-semibold text-blue-900">Jours de présence</h4>
                <p className="text-sm text-blue-700 mt-1">
                  Laissez vide pour que l'élève soit attendu tous les jours de service de l'école.
                </p>
              </div>
              <div className="p-4 flex flex-wrap gap-2">
                {JOURS_SEMAINE.map((jour) => {
                  const actif = (value.jours_semaine || []).includes(jour.value);
                  return (
                    <button
                      key={jour.value}
                      type="button"
                      onClick={() => toggleJour(jour.value)}
                      className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                        actif
                          ? 'border-[#246990] bg-[#246990] text-white'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {jour.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Période et statut */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Début (optionnel)</label>
              <Input
                type="date"
                value={value.date_debut || ''}
                onChange={(e) => setValue({ ...value, date_debut: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fin (optionnel)</label>
              <Input
                type="date"
                value={value.date_fin || ''}
                onChange={(e) => setValue({ ...value, date_fin: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
              <Select
                value={value.statut || 'actif'}
                onValueChange={(v) => setValue({ ...value, statut: v })}
              >
                <SelectTrigger className="bg-white border-gray-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Note interne (optionnelle)</label>
            <textarea
              value={value.observations || ''}
              onChange={(e) => setValue({ ...value, observations: e.target.value })}
              placeholder="Information utile pour l'équipe de restauration..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent"
            />
          </div>

          {/* Rappel facturation */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-start gap-3">
            <Info size={18} className="text-gray-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-700">
              {value.regime_facturation === 'forfait'
                ? "L'abonnement est facturé via la comptabilité existante (annexes famille). L'inscription ici ne crée aucun paiement."
                : "L'élève devra payer chaque journée de cantine à la caisse pour apparaître dans la feuille de pointage du jour."}
              {eleveSelectionne?.frais_cantine ? (
                <> Tarif de la classe : <strong>{formatMontant(eleveSelectionne.frais_cantine)}</strong>.</>
              ) : null}
            </p>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button variant="outline" size="default" className="flex-1" onClick={onClose} disabled={isSaving}>
              Annuler
            </Button>
            <Button
              size="default"
              className="flex-1 bg-[#246990] hover:bg-[#1f5a7d] text-white"
              onClick={onSubmit}
              disabled={isSaving || !value.eleve_id}
            >
              {isSaving ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save size={16} className="mr-2" />
                  {isEdit ? 'Enregistrer' : "Inscrire l'élève"}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalInscriptionCantine;
